import React, { useCallback, useEffect, useId, useRef } from 'react';
import './Modal.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container) {
  if (!container) return [];
  const nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
  );
}

function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  className = '',
  children,
  footer,
  initialFocusRef,
  role = 'dialog',
}) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const prevFocusRef = useRef(null);
  const generatedId = useId();
  const titleId = `ui-modal-title-${generatedId}`;
  const descriptionId = `ui-modal-description-${generatedId}`;

  // Tab / Shift+Tab focus trap only — Escape and click-outside close
  // paths are handled separately (window listener inside the effect
  // below, backdrop onClick). No reactive dependencies needed.
  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'Tab') return;

    const focusables = getFocusable(dialogRef.current);
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !dialogRef.current.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    prevFocusRef.current = document.activeElement;

    const timer = window.setTimeout(() => {
      const target =
        initialFocusRef?.current ||
        getFocusable(dialogRef.current).find((el) => el !== closeBtnRef.current) ||
        closeBtnRef.current;
      target?.focus();
    }, 0);

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', handleEscape);
      const prev = prevFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open, onClose, initialFocusRef]);

  if (!open) return null;

  return (
    <div
      className="ui-modal-backdrop"
      data-testid="ui-modal-backdrop"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className={`ui-modal ui-modal--${size} ${className}`.trim()}
        role={role}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="ui-modal__header">
          <div className="ui-modal__header-text">
            {title ? (
              <h2 id={titleId} className="ui-modal__title">
                {title}
              </h2>
            ) : null}
            {description ? (
              <p id={descriptionId} className="ui-modal__description">
                {description}
              </p>
            ) : null}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="ui-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="ui-modal__body">{children}</div>
        {footer ? <div className="ui-modal__footer">{footer}</div> : null}
      </div>
    </div>
  );
}

export default Modal;
